import React, { useContext, useEffect, useMemo, useState } from "react";
import { ConstantsContext } from "../index";
import CoinLogo from "./CoinLogo";
import PerformanceSection from "./PerformanceSection";

/* ============================ NEWS + AUTH ============================ */
function NewsAndAuth({ userDetails, _apiConnect }) {
  const [staticText] = useContext(ConstantsContext);
  const [cmdNewsFlash, setCmdNewsFlash] = useState([]);
  const [sortMode, setSortMode] = useState("alpha"); // "alpha" | "latest" | "sentiment"

  // --- API glue (same as your helper functions) ---
  function _api_get_pnl_public_callback(_data) {
    if (_data && _data.status_code === 2) {
      // We only need the news for this section, but you can keep more if desired
      setCmdNewsFlash(_data.cmd_news_flash || []);
    }
  }

  const _api_get_pnl_public = (_month = "") => {
    const data = [
      { key: "loggined_token", value: userDetails?.loggined_token },
      { key: "action", value: "get_pnl_public" },
      { key: "filterByMonthValue", value: _month },
    ];
    _apiConnect(staticText.path_darts, data, _api_get_pnl_public_callback);
  };

  useEffect(() => {
    _api_get_pnl_public("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // --- Sorting (alpha / latest / sentiment) ---
  const items = useMemo(() => {
    const list = [...(cmdNewsFlash || [])];
    if (sortMode === "alpha") {
      list.sort((a, b) => {
        const A = (a.coin_pair_name || "").localeCompare(b.coin_pair_name || "");
        if (A !== 0) return A;
        return new Date(b.date) - new Date(a.date);
      });
    } else if (sortMode === "latest") {
      list.sort((a, b) => new Date(b.date) - new Date(a.date));
    } else if (sortMode === "sentiment") {
      const rank = { bullish: 0, neutral: 1, bearish: 2 };
      list.sort((a, b) => {
        const ra = rank[(a.headline_sentiment || "neutral").toLowerCase()] ?? 1;
        const rb = rank[(b.headline_sentiment || "neutral").toLowerCase()] ?? 1;
        if (ra !== rb) return ra - rb;
        return new Date(b.date) - new Date(a.date);
      });
    }
    return list;
  }, [cmdNewsFlash, sortMode]);

  return (
    <section id="news" className="relative">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-6">
        <div className="grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-7">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-lg font-semibold tracking-tight">CMD News Flash</h2>
              <SortPills sortMode={sortMode} setSortMode={setSortMode} />
            </div>
            <NewsFeed items={items} />
          </div>
          <div className="lg:col-span-5">
            <PerformanceSection userDetails={userDetails} _apiConnect={_apiConnect} />
          </div>
        </div>
      </div>
    </section>
  );
}

function SortPills({ sortMode, setSortMode }) {
  const opts = [
    { key: "alpha", label: "Alphabetical" },
    { key: "latest", label: "Latest" },
    { key: "sentiment", label: "Sentiment" },
  ];
  return (
    <div className="flex items-center gap-1 text-xs">
      <span className="hidden sm:inline text-slate-400 mr-1">Sort by:</span>
      {opts.map((o) => (
        <button
          key={o.key}
          onClick={() => setSortMode(o.key)}
          className={`rounded-full px-3 py-1 border border-white/10 ${
            sortMode === o.key ? "bg-white/10" : "bg-transparent hover:bg-white/10"
          }`}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

function NewsFeed({ items, sortMode }) {
  const [expanded, setExpanded] = useState(false);

  // When user changes sort, collapse back to first 3 (filters still apply)
  useEffect(() => setExpanded(false), [sortMode]);

  const visible = useMemo(
    () => (expanded ? (items || []) : (items || []).slice(0, 3)),
    [items, expanded]
  );

  const sentimentKey = (s) => {
    const v = String(s || "").toLowerCase();
    if (v === "bullish") return "bullish";
    if (v === "bearish") return "bearish";
    return "neutral";
  };

  return (
    <>
      <ul className="space-y-3">
        {visible.map((n) => (
          <li
            key={n.id}
            className="rounded-2xl border border-white/10 bg-gradient-to-br from-slate-900/60 to-slate-900/20 p-4 hover:border-white/20 transition"
          >
            <div className="flex items-start gap-3">
              {/* coin logo */}
              <div className="mt-0.5">
                <CoinLogo coinPair={n.coin_pair_name} />
              </div>

              <div className="flex-1">
                {/* pair • time • link */}
                <div className="flex items-center justify-between">
                  <div className="text-xs uppercase tracking-wider text-slate-400 flex items-center gap-2">
                    <span className="font-semibold text-slate-200">{n.coin_pair_name}</span>
                    <span className="hidden sm:inline">•</span>
                    <span>{n.date_ago}</span>
                  </div>
                  <a
                    href={`https://cryptomarketdarts.com/public_prediction.php?id=${n.id}`}
                    target="_blank"
                    rel="noreferrer"
                    className="text-xs text-emerald-400 hover:text-emerald-300"
                  >
                    Details...
                  </a>
                </div>

                {/* sentiment dot + headline — centered to the text */}
                <div className="mt-1 flex items-center gap-2">
                  <span className={`sentiment-dot ${sentimentKey(n.headline_sentiment)}`} />
                  <p className="text-sm text-slate-200">{n.headline}</p>
                </div>
              </div>
            </div>
          </li>
        ))}
      </ul>

      {/* Show more / less */}
      {items && items.length > 3 ? (
        <div className="pt-2">
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className="text-xs text-emerald-400 hover:text-emerald-300"
          >
            {expanded ? "Show less" : `Show ${items.length - 3} more`}
          </button>
        </div>
      ) : null}

      {/* Keep your dot styles – move to global CSS if you prefer */}
      <style>{`
        .sentiment-dot {
          width: 10px;
          height: 10px;
          border-radius: 50%;
          display: inline-block;
          flex: 0 0 auto;
          border: 1px solid rgba(0, 0, 0, 0.25);
        }
        .sentiment-dot.bullish {
          background: #00b050;
          border-color: #00b050;
        }
        .sentiment-dot.bearish {
          background: #d9534f;
          border-color: #d9534f;
        }
        .sentiment-dot.neutral {
          background: #808080;
          border-color: #808080;
        }
      `}</style>
    </>
  );
}



/* ============================ MINI KPIs (uses real data) ============================ */
function MiniKPIs({ news }) {
  const signals = (news || []).length;
  const coverage = new Set((news || []).map((n) => n.coin_pair_name)).size;

  const stats = [
    { label: "Signals today", value: signals },
    { label: "Avg. fill time", value: "2m 31s" }, // keep as-is (no field provided)
    { label: "Coverage", value: `${coverage} coins` },
    { label: "Uptime", value: "99.99%" },
  ];

  return (
    <div className="rounded-2xl border border-white/10 bg-white/5 p-6">
      <h3 className="text-sm font-semibold mb-3">Today at a glance</h3>
      <dl className="grid grid-cols-2 gap-4">
        {stats.map((s) => (
          <div key={s.label} className="rounded-xl border border-white/5 bg-white/5 p-3">
            <dt className="text-xs text-slate-400">{s.label}</dt>
            <dd className="text-lg font-bold mt-0.5">{s.value}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

export { NewsAndAuth };
